// import components
import {
  Box,
  Button,
  LinearProgress,
  Tooltip,
  Typography
} from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import {
  StyledDoneText,
  StyledPreviewSummary,
  StyledProcedureDescription,
  StyledProcedureWarning,
  StyledProcedureWarningText,
  StyledStartRow
} from '../Servicedesk.styles';

// import hooks
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

// import queries & mutations
import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

// import context
import { DrawerContext } from 'components/shared/Drawer/DrawerContext';

// import types
import type { AxiosError } from 'axios';
import type { DutyLinesProgress } from '../../Realization/types';
import type { ActionToExecuteProps } from '../Servicedesk.types';

// import utils
import { formatByLocale, toOptionalDateString } from 'utils/date';
import EventBus from 'lib/EventBus';
import showError from 'components/shared/showError';

const eventBusUrl = '/servicedesk/revert-realization';

const ActionToExecute = ({
  procedureCode,
  startDate,
  endDate
}: ActionToExecuteProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { onInnerFormChange } = useContext(DrawerContext);
  const [progress, setProgress] = useState<DutyLinesProgress | null>(null);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const isInProgressRef = useRef(false);
  isInProgressRef.current = isInProgress;

  // Reuses the drawer's existing "unsaved changes" guard: while a run is in flight, closing
  // the drawer would unmount this component, silently drop the EventBus subscription that
  // flips the run to done, and let the operator reopen it and start a second, overlapping
  // run over the same period. Treating "in progress" as a dirty form makes the drawer ask
  // for confirmation before closing, same as it already does for MultiStepForm.
  useEffect(() => {
    onInnerFormChange?.(isInProgress);
  }, [isInProgress, onInnerFormChange]);

  const isValidPeriod = Boolean(startDate && endDate);
  const startDateParam = toOptionalDateString(startDate);
  const endDateParam = toOptionalDateString(endDate);

  const { data: preview } = queries.useSecureAdminProceduresServicedeskPreview(
    procedureCode,
    startDateParam,
    endDateParam,
    { enabled: isValidPeriod }
  );

  const { mutate: executeProcedure } =
    mutations.useSecureAdminProceduresServicedeskExecute();

  // EventBus matches listeners by reference (see EventBus.js), so the handler has to keep
  // one identity for the whole run - otherwise unregister silently leaves it subscribed.
  const onProgress = useCallback(
    (newProgress: DutyLinesProgress) => {
      setProgress(newProgress);
      if (newProgress?.hasFinished) {
        setIsInProgress(false);
        queryClient.invalidateQueries(
          queryKeys.secureAdminProceduresServicedeskRuns()
        );
      }
    },
    [queryClient]
  );

  // Subscribed only while a run is in flight; the cleanup unregisters the exact same
  // reference, also when the component unmounts mid-run.
  useEffect(() => {
    if (!isInProgress) return;

    EventBus.register(eventBusUrl, onProgress);
    return () => EventBus.unregister(eventBusUrl, onProgress);
  }, [isInProgress, onProgress]);

  // A newly picked period starts fresh: the previous run's finished/progress state shouldn't
  // carry over and keep the button disabled for a period that hasn't been executed yet.
  // Never while a run is still in flight though - that would re-enable Start and let the
  // operator launch a second, overlapping execution.
  useEffect(() => {
    if (isInProgressRef.current) return;

    setProgress(null);
  }, [startDateParam, endDateParam]);

  const onClickStart = () => {
    if (!isValidPeriod || isInProgress) return;

    setProgress(null);
    setIsInProgress(true);

    executeProcedure(
      {
        procedureCode,
        startDate: startDateParam,
        endDate: endDateParam
      },
      {
        onError: (error: unknown) => {
          setIsInProgress(false);
          showError(error as AxiosError);
        }
      }
    );
  };

  return (
    <Box data-testid="servicedesk-action-to-execute">
      <StyledProcedureDescription variant="body2">
        {t(`adminPortal.servicedesk.procedures.${procedureCode}.description`)}
      </StyledProcedureDescription>
      <StyledProcedureWarning>
        <Icon type="triangle-exclamation" color="warning" fontSize="small" />
        <StyledProcedureWarningText variant="body2">
          {t(`adminPortal.servicedesk.procedures.${procedureCode}.warning`)}
        </StyledProcedureWarningText>
      </StyledProcedureWarning>

      {isValidPeriod && (
        <StyledPreviewSummary>
          <Typography>
            {`${formatByLocale(startDate as Date)} ${t(
              'until'
            )} ${formatByLocale(endDate as Date)}`}
          </Typography>
          {preview && (
            <Typography>
              {t('adminPortal.servicedesk.actionToExecute.affectedDutyLines', {
                count: preview.affectedDutyLines ?? 0
              })}
            </Typography>
          )}
          {preview?.laterRealizedUntil && (
            <Tooltip
              title={
                t(
                  'adminPortal.servicedesk.actionToExecute.laterRealizedWarning',
                  {
                    date: preview.laterRealizedUntil
                  }
                ) || ''
              }
            >
              <Icon type="triangle-exclamation" color="warning" />
            </Tooltip>
          )}
        </StyledPreviewSummary>
      )}

      <StyledStartRow>
        {(!progress || progress.hasFinished) && (
          <Button
            size="small"
            variant="tonal"
            disabled={!isValidPeriod || isInProgress}
            onClick={onClickStart}
          >
            {t('adminPortal.servicedesk.actionToExecute.start')}
          </Button>
        )}
        {progress && !progress.hasFinished && (
          <LinearProgress
            variant="determinate"
            value={(progress.dutyLinesProgress / progress.dutyLinesTotal) * 100}
          />
        )}
        {progress?.hasFinished && (
          <StyledDoneText>
            {t('adminPortal.servicedesk.actionToExecute.done', {
              done: progress.dutyLinesProgress,
              total: progress.dutyLinesTotal
            })}
          </StyledDoneText>
        )}
      </StyledStartRow>
    </Box>
  );
};

export default ActionToExecute;
